/**
 * API Client for NestJS Backend Integration
 * Phase 3: Frontend-Backend Integration
 *
 * このファイルは、バックエンドAPIとの通信を担当するHTTPクライアントです。
 * - axios設定
 * - JWT認証ヘッダー
 * - エラーハンドリング（sonner toast連携）
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * APIクライアントクラス
 * シングルトンパターンで実装
 */
class ApiClient {
  private client: AxiosInstance;
  private static instance: ApiClient;

  private constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // リクエストインターセプター（JWT認証）
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // レスポンスインターセプター（エラーハンドリング）
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * シングルトンインスタンス取得
   */
  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * エラーハンドリング（sonner toast連携）
   */
  private handleError(error: AxiosError) {
    if (error.response) {
      const status = error.response.status;
      const message = (error.response.data as any)?.message || 'エラーが発生しました';

      switch (status) {
        case 400:
          toast.error(`入力エラー: ${message}`);
          break;
        case 401:
          toast.error('認証エラー: ログインしてください');
          // 認証エラー時はログイン画面へリダイレクト（将来的に実装）
          // window.location.href = '/login';
          break;
        case 403:
          toast.error('アクセス権限がありません');
          break;
        case 404:
          toast.error('データが見つかりませんでした');
          break;
        case 409:
          toast.error(`重複エラー: ${message}`);
          break;
        case 500:
          toast.error('サーバーエラーが発生しました');
          break;
        default:
          toast.error(`エラー: ${message}`);
      }
    } else if (error.request) {
      toast.error('サーバーに接続できません');
    } else {
      toast.error('予期しないエラーが発生しました');
    }
  }

  /**
   * GET リクエスト
   */
  public get<T>(url: string, params?: any) {
    return this.client.get<T>(url, { params });
  }

  /**
   * POST リクエスト
   */
  public post<T>(url: string, data?: any) {
    return this.client.post<T>(url, data);
  }

  /**
   * PUT リクエスト
   */
  public put<T>(url: string, data?: any) {
    return this.client.put<T>(url, data);
  }

  /**
   * PATCH リクエスト
   */
  public patch<T>(url: string, data?: any) {
    return this.client.patch<T>(url, data);
  }

  /**
   * DELETE リクエスト
   */
  public delete<T>(url: string) {
    return this.client.delete<T>(url);
  }

  /**
   * 認証トークン設定
   */
  public setAuthToken(token: string) {
    localStorage.setItem('auth_token', token);
  }

  /**
   * 認証トークンクリア
   */
  public clearAuthToken() {
    localStorage.removeItem('auth_token');
  }
}

// シングルトンインスタンスをエクスポート
export const apiClient = ApiClient.getInstance();
export default apiClient;
